import { defineStore } from "pinia";
import { markRaw } from "vue";
import posthog from "posthog-js";
import axios from "axios";
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import { monitoringCaptureError } from "@/plugins/monitoring";
import * as dsUtil from "@/components/DataSources/utilities";

export type UploadItemStatus = "queued" | "uploading" | "success" | "failed";

export interface UploadItem {
	id: number;
	file: File;
	fileName: string;
	destination: string;
	path: string;
	status: UploadItemStatus;
	error?: string;
}

interface UploadsState {
	items: UploadItem[];
	nextId: number;
	isRunning: boolean;
	trayOpen: boolean;
	// Increments whenever a run drains with at least one success, so views can refresh.
	completionTick: number;
	// Lets a confirmed sign-out skip UploadTray's beforeunload prompt, avoiding a double-prompt.
	bypassUnloadGuard: boolean;
}

// TODO: import MAX_UPLOAD_SIZE_BYTES from the SDK once signaloid/project-kea-cloud-api-sdk-issues#78 ships.
const UPLOAD_SIZE_LIMIT_BYTES = 5_368_709_120;
const GENERIC_UPLOAD_ERROR = "Upload failed. Please try again.";
const EMPTY_FILE_ERROR = "This file is empty. Add content to it and upload it again.";
const FILE_TOO_LARGE_ERROR = "This file exceeds the 5 GB limit. Upload a smaller file.";

function refusalReason(file: File): string | undefined {
	if (file.size === 0) {
		return EMPTY_FILE_ERROR;
	}
	if (file.size > UPLOAD_SIZE_LIMIT_BYTES) {
		return FILE_TOO_LARGE_ERROR;
	}
	return undefined;
}

function resolveUploadError(error: unknown): string {
	if (error instanceof SdkError) {
		if (error.code === ERROR_CODES.API_PAYLOAD_TOO_LARGE) {
			return FILE_TOO_LARGE_ERROR;
		}
		if (error.code === ERROR_CODES.API_UNSUPPORTED_MEDIA_TYPE) {
			return GENERIC_UPLOAD_ERROR;
		}
		// The backend's message names which limit was reached, so prefer it.
		const fallback =
			error.code === ERROR_CODES.API_FORBIDDEN
				? "Could not upload file due to tier restrictions."
				: GENERIC_UPLOAD_ERROR;
		return error.message || fallback;
	}
	if (axios.isAxiosError(error)) {
		// The SDK's upload() does a direct axios PUT to the S3 presigned URL;
		// a raw axios error here means that second hop failed (S3 rejection or network).
		return error.response
			? GENERIC_UPLOAD_ERROR
			: "Failed to upload the file. Make sure you have an active internet connection.";
	}
	if (error instanceof Error) {
		return error.message;
	}
	return "Unknown error occurred. Please try again.";
}

/**
 * Owns Signaloid Cloud Storage uploads independently of any dialog, so an upload
 * keeps running (and its result still lands) after the picker dialog closes or the
 * user navigates away. Runs the queue sequentially and exposes per-file state to the
 * global UploadTray.
 */
export const useUploadsStore = defineStore("uploads", {
	state: (): UploadsState => ({
		items: [],
		nextId: 1,
		isRunning: false,
		trayOpen: false,
		completionTick: 0,
		bypassUnloadGuard: false,
	}),
	getters: {
		hasActive: (state): boolean =>
			state.items.some((item) => item.status === "queued" || item.status === "uploading"),
		totalCount: (state): number => state.items.length,
		successCount: (state): number => state.items.filter((item) => item.status === "success").length,
		failedItems: (state): UploadItem[] => state.items.filter((item) => item.status === "failed"),
		retryableItems(): UploadItem[] {
			return this.failedItems.filter((item) => !refusalReason(item.file));
		},
		settledCount: (state): number =>
			state.items.filter((item) => item.status === "success" || item.status === "failed").length,
	},
	actions: {
		enqueue(files: File[], destination: string | undefined) {
			const dest = destination ?? "";
			for (const file of files) {
				const path = dsUtil.sanitizeStoragePath(dest ? `${dest}/${file.name}` : file.name);
				const refusal = refusalReason(file);
				this.items.push({
					id: this.nextId++,
					file: markRaw(file),
					fileName: file.name,
					destination: dest,
					path,
					status: refusal ? "failed" : "queued",
					error: refusal,
				});
			}
			this.trayOpen = true;
			this.run();
		},
		// Safe to call repeatedly: the isRunning guard keeps a single sequential loop.
		async run() {
			if (this.isRunning) {
				return;
			}
			this.isRunning = true;
			let succeeded = 0;
			try {
				let next: UploadItem | undefined;
				while ((next = this.items.find((item) => item.status === "queued"))) {
					next.status = "uploading";
					try {
						await sdk.files.upload(next.path, next.file);
						next.status = "success";
						succeeded++;
					} catch (error) {
						next.status = "failed";
						next.error = resolveUploadError(error);
						// 403 = tier/quota limit: expected, not a bug — keep it out of Sentry (matches
						// the tasks store). The type guard is load-bearing too: monitoringCaptureError
						// throws on a non-Error value, which would otherwise escape and halt the queue.
						const isExpectedRejection =
							error instanceof SdkError &&
							(error.code === ERROR_CODES.API_FORBIDDEN ||
								error.code === ERROR_CODES.API_PAYLOAD_TOO_LARGE);
						const isReportable = axios.isAxiosError(error) || error instanceof Error;
						if (isReportable && !isExpectedRejection) {
							monitoringCaptureError(error, "Upload files to user cloud storage");
						}
					}
				}
			} finally {
				this.isRunning = false;
				if (succeeded > 0) {
					posthog?.capture("cloud_storage_file_uploaded", { file_count: succeeded });
					this.completionTick++;
				}
			}
		},
		retry(id: number) {
			const item = this.retryableItems.find((entry) => entry.id === id);
			if (item) {
				item.status = "queued";
				item.error = undefined;
				this.run();
			}
		},
		retryAllFailed() {
			const retryable = this.retryableItems;
			for (const item of retryable) {
				item.status = "queued";
				item.error = undefined;
			}
			if (retryable.length > 0) {
				this.run();
			}
		},
		removeItem(id: number) {
			this.items = this.items.filter((item) => item.id !== id);
			if (this.items.length === 0) {
				this.trayOpen = false;
			}
		},
		clearFinished() {
			this.items = this.items.filter((item) => item.status === "queued" || item.status === "uploading");
			if (this.items.length === 0) {
				this.trayOpen = false;
			}
		},
	},
});
